'use client'
import * as React from 'react'
import { userPreferencesMutators } from '@basehub/data/replicache/mutators/repo-space/user-preferences/mutators/index'
import { useReplicacheQueryV2 } from './use-replicache-query'
import { useRepoReplicache } from './use-repo-replicache'
import { invariant } from '@epic-web/invariant'
import {
  userPreferencesSchema,
  Comment,
  EventBlock,
  ListBlock,
  TimeSeriesRange,
  VariantData,
  UserPreferences,
} from '@basehub/data/replicache/index'
import { useStorage } from '~/hooks/use-storage'
import { useUserStore } from '~/app/_store/user-store'
import { createStore, useStore } from 'zustand'

type CommitsFilters = {
  range?: { from: string; to: string } | null
  members?: string[] | null
}
export type UserPreferencesProps = {
  id: string | undefined
  favoriteBlocks: string[] | undefined
  collapsePropertiesPanel: boolean | undefined
  propertiesPanelMode: 'properties' | 'history'
  collapsibleGroups: Record<string, boolean>
  historyPanelFilters: CommitsFilters | undefined
  commitsFilters: CommitsFilters | undefined
  collapseDocumentPanel: boolean | undefined
  hideOnboardingGuides: boolean
  isLoading: boolean
  editMode: boolean
  listSelectedView: Record<ListBlock['id'], string /* view id */>
  eventSelectedRange: Record<EventBlock['id'], TimeSeriesRange>
  commentsRead: Record<Comment['id'], string /* timestamp */>
  selectedVariants: VariantData[]
  selectedThreads: UserPreferences['selectedThreads']
}

const createUserPreferencesStore = (
  initProps?: Partial<UserPreferencesProps>
) => {
  const DEFAULT_PROPS: UserPreferencesProps = {
    isLoading: true,
    id: undefined,
    collapseDocumentPanel: false,
    collapsePropertiesPanel: false,
    propertiesPanelMode: 'properties',
    historyPanelFilters: undefined,
    commitsFilters: undefined,
    hideOnboardingGuides: false,
    favoriteBlocks: undefined,
    editMode: false,
    listSelectedView: {},
    eventSelectedRange: {},
    commentsRead: {},
    selectedThreads: {},
    collapsibleGroups: {},
    selectedVariants: [],
  }
  return createStore<UserPreferencesProps>()(() => {
    return {
      ...DEFAULT_PROPS,
      ...initProps,
    }
  })
}

type UserPreferencesStore = ReturnType<typeof createUserPreferencesStore>

export const UserPreferencesContext =
  React.createContext<UserPreferencesStore | null>(null)

export const UserPreferencesProvider = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  const { data: repoReplicacheData } = useRepoReplicache()
  const optimisticUser = useUserStore((s) => s.optimisticUser)
  const userId = optimisticUser?.id

  const storeRef = React.useRef(createUserPreferencesStore())

  const [optimisticUserPreferences, setOptimisticUserPreferences] = useStorage(
    'localStorage',
    () => userPreferencesSchema.nullable(),
    `user-preferences-${userId}-${repoReplicacheData?.repoId}`,
    {
      initialValue: null,
      enabled: Boolean(userId && repoReplicacheData?.repoId),
    }
  )

  useReplicacheQueryV2({
    key: ['user-preferences', userId, repoReplicacheData?.repoId],
    replicache: repoReplicacheData?.replicache,
    enabled: Boolean(userId),
    initialData: repoReplicacheData?.initialUserPreferences,
    fetcher: async (tx) => {
      if (!userId) {
        throw new Error("shouldn't be enabled")
      }
      const userPreferences = await userPreferencesMutators._getUserPreferences(
        tx,
        userId
      )
      invariant(
        userPreferences,
        `userPreferences for user ${userId} should exist by this point.`
      )
      return userPreferencesSchema.parse(userPreferences)
    },
    onData: (d) => {
      setOptimisticUserPreferences(d)
      storeRef.current.setState({ ...d, isLoading: false })
    },
  })

  React.useEffect(() => {
    if (!optimisticUserPreferences) return
    storeRef.current.setState({
      ...optimisticUserPreferences,
      isLoading: false,
    })
  }, [optimisticUserPreferences])

  return (
    // eslint-disable-next-line react-compiler/react-compiler
    <UserPreferencesContext.Provider value={storeRef.current}>
      {children}
    </UserPreferencesContext.Provider>
  )
}

export function useUserPreferences<T>(
  selector: (state: UserPreferencesProps) => T
): T {
  const store = React.useContext(UserPreferencesContext)
  if (!store) {
    throw new Error('Missing UserPreferencesContext.Provider in the tree')
  }
  return useStore(store, selector)
}
